import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import { LoadingButton } from '@mui/lab';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Box, Chip, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { SectionCard } from '../../../components/Section';
import { TREASURY_ALPACA_AGREEMENTS } from '../../../constants/treasury';
import { usePermissions } from '../../../hooks';
import { useAcceptTreasuryAgreementsMutation } from '../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryCompanySetup } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../utils/treasury';
import { TreasuryOwnerAgreementSection } from './components/TreasuryOwnerAgreementSection';

type TreasuryAgreementsStepProps = {
  setup: TreasuryCompanySetup;
  onDone: () => void;
};

/**
 * Step 7 — the two Alpaca agreements. Either side may accept, whoever is first: a Madkhol admin here, or any company
 * HR admin on the HR dashboard. The backend records who, when and the caller's IP, and sends that time and IP to Alpaca
 * as the owner's signature. A second acceptance changes nothing; saving the KYB again does not clear it.
 */
export const TreasuryAgreementsStep = ({ setup, onDone }: TreasuryAgreementsStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canAccept = usePermissions({ items: [permissions.Treasury.updateKyb] });
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [acceptTreasuryAgreements, { isLoading: isAccepting }] = useAcceptTreasuryAgreementsMutation();

  const accept = async () => {
    try {
      await acceptTreasuryAgreements(setup.companyId).unwrap();
      enqueueSnackbar('Alpaca agreements accepted!', { variant: 'success' });
      onDone();
    } catch (error) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Agreements accept error!'), { variant: 'error' });
    }
  };

  return (
    <Stack spacing={2}>
      <TreasuryOwnerAgreementSection setup={setup} />

      <SectionCard
        title="Company Agreements"
        description={`Accepted for the company and sent to Alpaca as the signature of the owner, ${
          setup.employee?.full_name_en ?? 'the owner'
        }. Your name, the time and your IP are recorded; an HR admin can accept them instead — whoever is first counts.`}
        action={<Chip size="small" color="warning" label="Not accepted" />}
      >
        <Stack spacing={2}>
          <List dense disablePadding>
            {TREASURY_ALPACA_AGREEMENTS.map((agreement) => (
              <ListItem key={agreement.name} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <DescriptionIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={agreement.label} secondary={agreement.description} />
              </ListItem>
            ))}
          </List>
          <FormControlLabel
            control={
              <Checkbox
                id="agreementsConfirmed"
                name="agreementsConfirmed"
                checked={isConfirmed}
                onChange={(event) => setIsConfirmed(event.target.checked)}
              />
            }
            label={<Typography variant="body2">I confirm the company accepts both Alpaca agreements</Typography>}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isAccepting}
              startIcon={<GavelIcon />}
              onClick={accept}
              disabled={!isConfirmed || isAccepting || !canAccept}
            >
              Accept Agreements
            </LoadingButton>
          </Box>
        </Stack>
      </SectionCard>
    </Stack>
  );
};
